import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore, selectUser, canAccessMenuSite, hasMenu, type MenuKey } from "@safers/auth";
import { AlarmInboxBell } from "@/components/AlarmInboxBell";
import { useToastStore } from "@/stores";
import { useCurrentSiteId } from "@/hooks/useCurrentSiteId";
import { readLastSite } from "@/lib/lastVisit";
import { areaOf, cctvLivePath, controlPath, healthPath, ME_PATH } from "@/routes/paths";

interface TabDef {
  to: string;
  label: string;
  isActive: (pathname: string) => boolean;
  icon: (active: boolean) => React.ReactNode;
  menu?: MenuKey;
}

const LOCKED_COLOR = "#8A8A8A";

const LOCKED_MARK = "#DC2626";

function LockedSlash() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <line
        x1="25"
        y1="2"
        x2="75"
        y2="98"
        stroke="var(--surface-base)"
        strokeWidth="3"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
      <line
        x1="25"
        y1="2"
        x2="75"
        y2="98"
        stroke={LOCKED_MARK}
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LockBadge() {
  return (
    <svg
      className="pointer-events-none absolute -right-1.5 -top-1"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M12 1.5A4.5 4.5 0 0 0 7.5 6v3H6v13h12V9h-1.5V6A4.5 4.5 0 0 0 12 1.5Zm2.5 7.5h-5V6a2.5 2.5 0 0 1 5 0v3Z"
        fill={LOCKED_MARK}
        stroke="var(--surface-base)"
        strokeWidth="2.5"
        paintOrder="stroke"
      />
    </svg>
  );
}

function iconProps(active: boolean) {
  return {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: active ? 1.9 : 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}

function areaTabs(id: string | null): TabDef[] {
  return [
    {
      to: controlPath(id),
      label: "관제",
      isActive: (p) => areaOf(p) === "control",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
          <path d="M8.5 20.5h7M12 16.5v4" />
        </svg>
      ),
    },
    {
      to: healthPath(id),
      label: "보건관리",
      menu: "HEALTH",
      isActive: (p) => areaOf(p) === "health",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M20.4 5.9a5 5 0 0 0-7.1 0L12 7.2l-1.3-1.3a5 5 0 1 0-7.1 7.1l1.3 1.3L12 21.4l7.1-7.1 1.3-1.3a5 5 0 0 0 0-7.1Z" />
        </svg>
      ),
    },
    {
      to: cctvLivePath({ siteId: id }),
      label: "CCTV",
      menu: "CCTV",
      isActive: (p) => areaOf(p) === "cctv",
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M3 8.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z" />
          <path d="m15 11 5-3v8l-5-3v-2Z" />
        </svg>
      ),
    },
  ];
}

const ACCOUNT_TAB: TabDef = {
  to: ME_PATH,
  label: "내정보",
  isActive: (p) => p === ME_PATH,
  icon: (active) => (
    <svg {...iconProps(active)}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </svg>
  ),
};

const FAB_SIZE = "3.25rem";
const PROTRUDE = "1rem";
const NOTCH_CENTER_Y = "0.625rem";
const NOTCH_RADIUS = "1.9375rem";
const NOTCH_WIDTH = "3.875rem";
const ARC_BOX_HEIGHT = "2.5625rem";
const ARC_OFFSET = "-1.3125rem";
const FAB_TOP = "-1.5rem";
const FAB_INSET = "0.3125rem";
const NOTCH_MASK = `radial-gradient(circle ${NOTCH_RADIUS} at 50% ${NOTCH_CENTER_Y}, transparent 98%, #000 100%)`;

function useSiteId(): string | null {
  const id = useCurrentSiteId();
  return id == null ? null : String(id);
}

export function BottomTabBar() {
  const { pathname } = useLocation();
  const routeSiteId = useSiteId();
  const user = useAuthStore(selectUser);
  const username = user?.username ?? null;

  const [lastSiteId, setLastSiteId] = useState(() => routeSiteId ?? readLastSite(username));
  if (routeSiteId !== null && routeSiteId !== lastSiteId) setLastSiteId(routeSiteId);
  const siteId = routeSiteId ?? (pathname === ACCOUNT_TAB.to ? lastSiteId : null);

  const tabs = [...areaTabs(siteId), ACCOUNT_TAB];
  const allowed = (menu: MenuKey) =>
    siteId ? canAccessMenuSite(user, siteId, menu) : hasMenu(user, menu);

  const enabled = (tab: TabDef) => !tab.menu || allowed(tab.menu);

  const explain = (tab: TabDef) => {
    const what = siteId ? `이 현장의 ${tab.label}` : tab.label;
    useToastStore.getState().toast.info(`${what} 권한이 없습니다. 관리자에게 문의하세요.`);
  };

  const TAB_COLOR = {
    locked: LOCKED_COLOR,
    active: "var(--brand-primary)",
    idle: "var(--text-tertiary)",
  } as const;

  function renderTab(tab: TabDef) {
    const active = tab.isActive(pathname);
    const on = enabled(tab);
    const color = TAB_COLOR[!on ? "locked" : active ? "active" : "idle"];
    const body = (
      <>
        <span className="relative flex">
          {tab.icon(active)}
          {!on && <LockBadge />}
        </span>
        <span
          className="text-[0.625rem] leading-[0.875rem]"
          style={{ fontWeight: active ? 600 : 500 }}
        >
          {tab.label}
        </span>
        {!on && <LockedSlash />}
      </>
    );
    const cls = "relative flex flex-1 flex-col items-center justify-center gap-[0.1875rem]";
    return on ? (
      <Link key={tab.to} to={tab.to} className={cls} style={{ color }}>
        {body}
      </Link>
    ) : (
      <button
        key={tab.to}
        type="button"
        aria-disabled="true"
        onClick={() => explain(tab)}
        className={`${cls} cursor-not-allowed`}
        style={{ color }}
        title="이 현장의 해당 기능 권한이 없습니다"
      >
        {body}
      </button>
    );
  }

  const half = Math.ceil(tabs.length / 2);
  const left = tabs.slice(0, half);
  const right = tabs.slice(half);

  return (
    <div className="relative z-40 flex-none" style={{ marginTop: `-${PROTRUDE}` }}>
      <div className="pointer-events-none" style={{ height: PROTRUDE }} aria-hidden="true" />

      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute inset-0 border-t border-[var(--border-default)] bg-[var(--surface-base)]"
          style={{ maskImage: NOTCH_MASK, WebkitMaskImage: NOTCH_MASK }}
        />
        <div
          className="absolute left-0 right-0 mx-auto overflow-hidden"
          style={{ top: 0, width: NOTCH_WIDTH, height: ARC_BOX_HEIGHT }}
          aria-hidden="true"
        >
          <div
            className="rounded-full border border-[var(--border-default)]"
            style={{ width: NOTCH_WIDTH, height: NOTCH_WIDTH, marginTop: ARC_OFFSET }}
          />
        </div>

        <nav
          className="relative flex items-stretch px-2 pt-2"
          style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex flex-1">{left.map(renderTab)}</div>
          <div className="relative flex-none" style={{ width: NOTCH_WIDTH }}>
            <div className="absolute" style={{ top: FAB_TOP, left: FAB_INSET, width: FAB_SIZE }}>
              <AlarmInboxBell variant="fab" />
            </div>
          </div>
          <div className="flex flex-1">{right.map(renderTab)}</div>
        </nav>
      </div>
    </div>
  );
}
